import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, renderSlot as _renderSlot, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-08574254"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["test-id"]
const _hoisted_2 = { class: "audio-player-content" }
const _hoisted_3 = { class: "audio-player-button activity-button" }
const _hoisted_4 = { class: "audio-player-button-inner activity-button-inner" }
const _hoisted_5 = {
  class: "pause-icon",
  key: "playing"
}
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "glyphicon glyphicon-pause" }, null, -1))
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = {
  class: "complete-icon",
  key: "complete"
}
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "glyphicon glyphicon-repeat" }, null, -1))
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = {
  class: "play-icon",
  key: "paused"
}
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "glyphicon glyphicon-play" }, null, -1))
const _hoisted_13 = [
  _hoisted_12
]
const _hoisted_14 = { class: "activity-description" }
const _hoisted_15 = { class: "audio-controls-wrapper" }
const _hoisted_16 = { class: "audio-controls" }
const _hoisted_17 = {
  id: "current-time",
  class: "time"
}
const _hoisted_18 = {
  id: "duration",
  class: "time"
}
const _hoisted_19 = ["max", "value"]
const _hoisted_20 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("br", null, null, -1))

import { Howl } from "howler";
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import { debug as d } from "debug";
import { Analytics } from "../services/analytics/analytics";

interface AudioSession {
  audioId: string;
  sound: Howl;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'audio-player',
  props: {
    audioId: {},
    cmsAudioSrc: {},
    trackTitle: {},
    albumTitle: {},
    isComplete: { type: Boolean },
    onListenEnd: { type: Function }
  },
  setup(__props: any) {

const debug = d("cs.AudioPlayerComponent");

let currentAudio: AudioSession | null = null;

const props = __props;

const sound = ref<Howl | null>(null);
const isPlaying = ref(false);
const hasBeenPlayed = ref(false);
const hasJustEnded = ref(false);
const position = ref<number | Howl>(0);
let intervalId;

watch(isPlaying, (newIsPlaying) => {
  if (intervalId !== undefined) {
    clearInterval(intervalId);
  }
  if (newIsPlaying) {
    intervalId = setInterval(() => {
      position.value = sound.value?.seek() ?? 0;
    }, 1000);
  }
});

const onPlay = () => {
  debug(`onPlay - ${props.audioId}`);
  isPlaying.value = true;
};

const onPause = () => {
  debug(`onPause - ${props.audioId}`);
  isPlaying.value = false;
};

const onEnd = () => {
  debug(`onEnd - ${props.audioId}`);
  Analytics.action("Finished Audio", "AudioPlayer", {
    audioId: props.audioId,
    audioTrackTitle: props.trackTitle,
  });
  isPlaying.value = false;
  hasJustEnded.value = true;
  if (props.onListenEnd) {
    props.onListenEnd();
  }
};

const onPlayError = () => {
  debug(`onPlayError - ${props.audioId}`);
  isPlaying.value = false;
};

const positionHandler = () => {
  if (props.isComplete) {
    position.value = Math.floor(sound.value?.duration() ?? 0);
  }
};

const updateTime = () => {
  if (!sound.value?.seek()) {
    position.value = sound.value?.seek() ?? 0;
  }
};

onMounted(async () => {
  debug("Running mounted");
  await new Promise<void>((resolve, reject) => {
    sound.value = new Howl({
      src: [props.cmsAudioSrc],
      html5: true,
      format: ["mp3"],
      onplay: onPlay,
      onload: () => {
        resolve();
      },
      onseek: updateTime,
      onplayerror: onPlayError,
      onloaderror: (soundId, err) => {
        reject(err);
      },
      onpause: onPause,
      onend: onEnd,
    });
  });
  debug("Finished mounted handler");
  positionHandler();
});

onBeforeUnmount(() => {
  debug("beforeDestroy");
  if (sound.value) {
    sound.value.unload();
    if (currentAudio && currentAudio.audioId === props.audioId) {
      currentAudio = null;
    }
  }
});

const playOrPause = () => {
  if (!sound.value) {
    return;
  }

  if (currentAudio && currentAudio.audioId !== props.audioId && currentAudio.sound.playing()) {
    currentAudio.sound.pause();
    currentAudio.sound.seek(0);
  }

  currentAudio = {
    audioId: props.audioId,
    sound: sound.value,
  };

  if (sound.value.playing()) {
    sound.value.pause();
  } else {
    sound.value.play();
    if (!hasBeenPlayed.value) {
      Analytics.action("Played Audio", "AudioPlayer", {
        audioId: props.audioId,
        audioTrackTitle: props.trackTitle,
      });
      hasBeenPlayed.value = true;
      if (props.isComplete) {
        position.value = sound.value.duration();
      }
    }
  }
};

const seekSlider = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const eventAsNumber = parseInt(target.value, 10);
  sound.value?.seek(eventAsNumber);
  position.value = eventAsNumber;
};

const conversion = (value: number): string => {
  let minute: string | number = Math.floor(value / 60);
  minute = minute.toString().length === 1 ? `0${minute}` : minute;
  let second: string | number = Math.floor(value % 60);
  second = second.toString().length === 1 ? `0${second}` : second;
  return `${minute}:${second}`;
};

const duration = computed(() => conversion(Math.floor(sound.value?.duration() ?? 0)));

const setSliderMax = computed(() => Math.floor(sound.value?.duration() ?? 0));

const setSliderPosition = computed(() => {
  if (position.value === setSliderMax.value) {
    return setSliderMax.value;
  }
  return position.value;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["audio-player", { complete: _ctx.isComplete, playing: isPlaying.value }]),
    "test-id": 'audio-' + _ctx.audioId
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: "audio-player-button-wrapper activity-button-wrapper",
        onClick: playOrPause
      }, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("span", _hoisted_4, [
            _createVNode(_Transition, {
              name: "fade",
              mode: "out-in"
            }, {
              default: _withCtx(() => [
                (isPlaying.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_5, _hoisted_7))
                  : (_ctx.isComplete)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, _hoisted_10))
                    : (_openBlock(), _createElementBlock("span", _hoisted_11, _hoisted_13))
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_14, [
        _renderSlot(_ctx.$slots, "default")
      ])
    ]),
    _createElementVNode("div", _hoisted_15, [
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("span", _hoisted_17, _toDisplayString(conversion(position.value)), 1),
        _createElementVNode("span", _hoisted_18, _toDisplayString(duration.value), 1)
      ]),
      _createElementVNode("input", {
        type: "range",
        class: "slider",
        max: setSliderMax.value,
        value: setSliderPosition.value,
        onClick: seekSlider,
        onChange: seekSlider
      }, null, 40, _hoisted_19)
    ]),
    _hoisted_20
  ], 10, _hoisted_1))
}
}

})